import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { StyledChatBox, Message, Avatar } from '../styles/ChatBoxStyles';
import { getImageUrl } from '../utils/mediaUrl';

// Styled span for hoverable words
const SpanHover = styled.span`
    transition: opacity 0.1s;
    padding: 0.2em 0;
    cursor: inherit;

    &::selection {
        background: transparent;
        color: inherit;
    }

    &:hover {
        opacity: 0.7;
    }

    &.highlighted {
        background: ${({ theme }) => `rgba(${theme.colorTranslate}, 1)`};
        background: ${({ theme }) => `color(display-p3 ${theme.pcolorTranslate} / 1)`};
        color: rgba(35, 34, 18, 0.9);
        color: color(display-p3 0.136 0.133 0.0793 / 0.9);

        &:hover {
            opacity: 1;
        }
    }
`;

// Render text content with clickable spans for captions
const renderClickableText = (content, isCaption) => {
    if (!isCaption || typeof content !== 'string') {
        return content;
    }

    return content.split(' ').map((word, i) => (
        <SpanHover key={i} data-index={i}>
            {word}{' '}
        </SpanHover>
    ));
};

const ChatBox = ({ messages, hasVideo = false, shouldBlur = false }) => {
    const chatBoxRef = useRef(null);
    const [messageCount, setMessageCount] = useState(messages.length);
    const prevMessagesLength = useRef(messages.length);

    useEffect(() => {
        setMessageCount(messages.length);
    }, [messages]);

    // Handle auto-scroll to bottom when messages change
    useEffect(() => {
        if (messages.length > prevMessagesLength.current) {
            if (chatBoxRef.current) {
                chatBoxRef.current.scrollTop = 0;
            }
            prevMessagesLength.current = messages.length;
        }
    }, [messages]);

    return (
        <div
            style={{
                filter: shouldBlur ? 'blur(0.2em)' : 'none',
                WebkitFilter: shouldBlur ? 'blur(0.2em)' : 'none',
                opacity: shouldBlur ? 0.3 : 1,
                transition: '0.3s',
            }}
        >
        <StyledChatBox ref={chatBoxRef} $hasVideo={hasVideo}>
            <div className="message-container-parent">
                <AnimatePresence initial={false} mode="popLayout">
                    {hasVideo && messages.length === 0 && (
                        <motion.div
                            key="waiting-for-captions"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <Message $sender="ai">
                                <Avatar $sender="ai">
                                    <div>
                                        <img
                                            src={getImageUrl("mask-bjo.svg")}
                                            alt=""
                                        />
                                    </div>
                                </Avatar>
                                Point your camera at the captions
                            </Message>
                            <Message $sender="ai">
                                <Avatar $sender="ai">
                                    <div>
                                        <img
                                            src={getImageUrl("mask-bjo.svg")}
                                            alt=""
                                        />
                                    </div>
                                </Avatar>
                                Waiting for captions...
                            </Message>
                        </motion.div>
                    )}
                    {messages.map((message, index) => {
                        const currentDelay =
                            messageCount > 0
                                ? ((index % messageCount) + 2) * 0.06
                                : (index + 1) * 0.05;

                        // Check if previous message was a caption
                        const prevMessage =
                            index > 0 ? messages[index - 1] : null;
                        const followsCaption =
                            prevMessage?.sender === 'caption';

                        const isCaptionSender = message.sender === 'caption';
                        // Use message.isCaption if set, otherwise fall back to sender check
                        // userHomeLang messages should never be highlightable
                        const canHighlight = message.sender === 'userHomeLang'
                            ? false
                            : (message.isCaption !== undefined ? message.isCaption : isCaptionSender);

                        return (
                            <motion.div
                                layout="position"
                                key={message.id || `${message.sender}-${index}`}
                                initial={{ opacity: 0, y: 50 }}
                                animate={
                                    message.justUpdated
                                        ? {
                                              opacity: 1,
                                              y: 0,
                                              scale: [1, 2, 1],
                                          }
                                        : { opacity: 1, y: 0, scale: 1 }
                                }
                                exit={
                                    message.type === 'starting'
                                        ? {
                                              opacity: 0,
                                              transition: {
                                                  duration: 0,
                                              },
                                          }
                                        : undefined
                                }
                                transition={{
                                    type: 'spring',
                                    bounce: 0.2,
                                    stiffness: 180,
                                    damping: 21,
                                    duration: 0.3,
                                    delay: message.justUpdated
                                        ? 0
                                        : currentDelay,
                                    layout: {
                                        type: 'spring',
                                        bounce: 0.2,
                                        stiffness: 170,
                                        damping: 21,
                                        duration: 0.4,
                                    },
                                    scale: message.justUpdated
                                        ? {
                                              type: 'spring',
                                              stiffness: 300,
                                              damping: 20,
                                              duration: 0.5,
                                          }
                                        : {},
                                }}
                            >
                                <Message
                                    $sender={message.sender || 'caption'}
                                    type={message.type}
                                    $followsCaption={followsCaption}
                                    $marginTop={message.marginTop}
                                    data-translatable={canHighlight ? 'true' : undefined}
                                    data-message-content={canHighlight ? 'true' : undefined}
                                    data-source-language={canHighlight && message.sourceLanguage
                                        ? message.sourceLanguage
                                        : undefined}
                                    data-analytics-session={canHighlight && message.analyticsSessionId
                                        ? message.analyticsSessionId
                                        : undefined}
                                >
                                    {message.sender === 'ai' && (
                                        <Avatar $sender="ai">
                                            <div><img src={getImageUrl("mask-bjo.svg")} alt="" /></div>
                                        </Avatar>
                                    )}
                                    {(message.sender === 'user' || message.sender === 'userHomeLang') && (
                                        <Avatar $sender={message.sender}>
                                            <div><img src={getImageUrl("mask-you.svg")} alt="" /></div>
                                        </Avatar>
                                    )}
                                    {renderClickableText(message.content, canHighlight)}
                                </Message>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        </StyledChatBox>
        </div>
    );
};

export default ChatBox;
